/**
 * BackgroundControls
 * Background settings types, presets, form, and persistence utilities.
 *
 * The inline form (BackgroundSettingsForm) lives in /settings.
 * App.tsx reads settings from localStorage and listens for
 * 'lukhas:bg-settings-changed' events for live preview.
 */
import { useEffect } from 'react'

export interface BackgroundSettings {
  oceanOpacity: number        // 0.1–1.0   default 0.62
  oceanRingIntensity: number  // 0.0–2.5   default 1.0 (multiplier on depthColors alphas)
  globeOpacity: number        // 0.0–1.5   default 1.0 (multiplier on chapter opacity)
  globeScale: number          // 0.5–1.5   default 1.0 (multiplier on chapter scale)
  globeBrightness: number     // 0.3–1.5   default 1.0 (multiplier on chapter brightness)
  globeRotationSpeed: number  // 0.0–0.006 default 0.001
}

// ── Named presets ─────────────────────────────────────────────────────────────

export interface BgPreset {
  id: string
  label: string
  description: string
  settings: BackgroundSettings
}

/**
 * Vivid matches the look of the original lukhas.ai dev server (port 3005)
 * where the ocean canvas is at full opacity with double ring intensity.
 * This is the richest, most immersive look.
 */
export const BG_PRESETS: BgPreset[] = [
  {
    id: 'vivid',
    label: 'Vivid',
    description: 'Rich ocean rings, full brightness — the 3005 look',
    settings: {
      oceanOpacity: 1.0,
      oceanRingIntensity: 2.2,
      globeOpacity: 1.1,
      globeScale: 1.0,
      globeBrightness: 1.1,
      globeRotationSpeed: 0.001,
    },
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Default — subtle ocean, gentle globe',
    settings: {
      oceanOpacity: 0.62,
      oceanRingIntensity: 1.0,
      globeOpacity: 1.0,
      globeScale: 1.0,
      globeBrightness: 1.0,
      globeRotationSpeed: 0.001,
    },
  },
  {
    id: 'subtle',
    label: 'Subtle',
    description: 'Quiet background, content first',
    settings: {
      oceanOpacity: 0.30,
      oceanRingIntensity: 0.6,
      globeOpacity: 0.7,
      globeScale: 0.9,
      globeBrightness: 0.8,
      globeRotationSpeed: 0.0008,
    },
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Near-invisible — focus mode',
    settings: {
      oceanOpacity: 0.12,
      oceanRingIntensity: 0.3,
      globeOpacity: 0.35,
      globeScale: 0.8,
      globeBrightness: 0.55,
      globeRotationSpeed: 0.0004,
    },
  },
  {
    id: 'deep',
    label: 'Deep',
    description: 'Dark ocean, slow drift, cinematic',
    settings: {
      oceanOpacity: 0.85,
      oceanRingIntensity: 1.6,
      globeOpacity: 0.8,
      globeScale: 1.1,
      globeBrightness: 0.75,
      globeRotationSpeed: 0.0004,
    },
  },
]

export const DEFAULT_BG_SETTINGS: BackgroundSettings = BG_PRESETS[0].settings  // Vivid as default

export const BG_SETTINGS_LS_KEY = 'lukhas_bg_settings'
export const BG_SETTINGS_EVENT  = 'lukhas:bg-settings-changed'

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Return the preset whose settings exactly match the given settings, or null. */
export function matchPreset(settings: BackgroundSettings): BgPreset | null {
  return BG_PRESETS.find(p =>
    Object.keys(p.settings).every(
      k => Math.abs(p.settings[k as keyof BackgroundSettings] - settings[k as keyof BackgroundSettings]) < 0.001
    )
  ) ?? null
}

// ── Preset strip ──────────────────────────────────────────────────────────────

interface PresetStripProps {
  current: BackgroundSettings
  onSelect: (settings: BackgroundSettings) => void
}

function PresetStrip({ current, onSelect }: PresetStripProps) {
  const active = matchPreset(current)

  return (
    <div className="flex gap-2 flex-wrap">
      {BG_PRESETS.map(preset => {
        const isActive = active?.id === preset.id
        return (
          <button
            key={preset.id}
            title={preset.description}
            onClick={() => onSelect(preset.settings)}
            className={`
              px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
              ${isActive
                ? 'bg-cyan-500/20 border-cyan-400/60 text-cyan-300'
                : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80 hover:border-white/20'
              }
            `}
          >
            {preset.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Slider row ────────────────────────────────────────────────────────────────

interface SliderRowProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  decimals?: number
  onChange: (v: number) => void
}

function SliderRow({ label, value, min, max, step, decimals = 2, onChange }: SliderRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/60">{label}</span>
        <span className="text-sm text-white/40 tabular-nums w-14 text-right">
          {value.toFixed(decimals)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full cursor-pointer"
        style={{ accentColor: '#22d3ee' }}
      />
    </div>
  )
}

// ── Inline form (used in /settings page) ─────────────────────────────────────

interface BackgroundSettingsFormProps {
  settings: BackgroundSettings
  onChange: (s: BackgroundSettings) => void
}

export function BackgroundSettingsForm({ settings, onChange }: BackgroundSettingsFormProps) {
  const set = <K extends keyof BackgroundSettings>(key: K, value: BackgroundSettings[K]) => {
    onChange({ ...settings, [key]: value })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Preset selector */}
      <div className="flex flex-col gap-2">
        <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Presets</span>
        <PresetStrip current={settings} onSelect={onChange} />
      </div>

      <div className="border-t border-white/5" />

      {/* Ocean */}
      <div className="flex flex-col gap-4">
        <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Ocean</span>
        <SliderRow
          label="Opacity"
          value={settings.oceanOpacity}
          min={0.05} max={1.0} step={0.01}
          onChange={(v) => set('oceanOpacity', v)}
        />
        <SliderRow
          label="Ring Intensity"
          value={settings.oceanRingIntensity}
          min={0.0} max={2.5} step={0.05}
          onChange={(v) => set('oceanRingIntensity', v)}
        />
      </div>

      <div className="border-t border-white/5" />

      {/* Globe */}
      <div className="flex flex-col gap-4">
        <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Globe</span>
        <SliderRow
          label="Opacity"
          value={settings.globeOpacity}
          min={0.0} max={1.5} step={0.05}
          onChange={(v) => set('globeOpacity', v)}
        />
        <SliderRow
          label="Scale"
          value={settings.globeScale}
          min={0.4} max={1.6} step={0.05}
          onChange={(v) => set('globeScale', v)}
        />
        <SliderRow
          label="Brightness"
          value={settings.globeBrightness}
          min={0.2} max={1.6} step={0.05}
          onChange={(v) => set('globeBrightness', v)}
        />
        <SliderRow
          label="Rotation Speed"
          value={settings.globeRotationSpeed}
          min={0.0} max={0.006} step={0.0002}
          decimals={4}
          onChange={(v) => set('globeRotationSpeed', v)}
        />
      </div>
    </div>
  )
}

// ── Persistence hook ──────────────────────────────────────────────────────────

/**
 * Loads background settings from localStorage on mount.
 * Saves + dispatches 'lukhas:bg-settings-changed' whenever settings change
 * so App.tsx can update live without a page reload.
 */
export function useBgSettingsPersistence(
  settings: BackgroundSettings,
  setSettings: (s: BackgroundSettings) => void
) {
  // Load on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(BG_SETTINGS_LS_KEY)
      if (saved) setSettings({ ...DEFAULT_BG_SETTINGS, ...JSON.parse(saved) })
    } catch { /* ignore */ }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Save + broadcast on every change
  useEffect(() => {
    try {
      localStorage.setItem(BG_SETTINGS_LS_KEY, JSON.stringify(settings))
      window.dispatchEvent(new CustomEvent(BG_SETTINGS_EVENT))
    } catch { /* ignore */ }
  }, [settings])
}
